import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import AudioComment from 'components/AudioComment/AudioComment';
import SwiperCore, { Mousewheel, Pagination } from 'swiper';
import 'swiper/swiper-bundle.css';
import axios from 'axios';
import { apiRequest } from 'utils/api';
import { fadeOut } from 'utils/animation';
import { useDispatch, useSelector } from 'react-redux';
import { setIsLoading } from 'features/loading/loadingSlice';
import { toggleAuthorize, toggleSettings } from 'features/auth/authSlice';
import {
    toggleUserProfile,
    setUser,
    updatePublisherId,
    updateSlidePage,
} from 'features/userProfile/userProfileSlice';
import { toggleTextComment, setPostId } from 'features/textComment/textCommentSlice';
import PostSlide from 'components/PostSlide/PostSlide';
import Authorize from '../Authorize/Authorize';
import ReportLightbox from './ReportLightbox';
import './styles.css';

// Ініціалізація додаткових модулів Swiper
SwiperCore.use([Mousewheel, Pagination]);

function Swiper_block({ setCameraOn }) {
    const [slides, setSlides] = useState([]);
    const [page, setPage] = useState(1);
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const publisherId = useSelector((state) => state.user.slidesPublisherId);
    const slidePage = useSelector((state) => state.user.slidePage);
    const [likedPosts, setLikedPosts] = useState([]); // Стан для збереження лайків постів
    const [isLiking, setIsLiking] = useState({}); // Стан для відстеження процесу лайкування кожного слайда
    const [isDisliking, setIsDisliking] = useState({}); // Стан для відстеження процесу дизлайкування кожного слайда
    const [isUIVisible, setIsUIVisible] = useState(true); // State for UI visibility

    const [activeIndex, setActiveIndex] = useState(0);
    const [currentPostId, setCurrentPostId] = useState(0);

    const [audioComments, setAudioComments] = useState(false);

    const [isReportBoxVisible, setIsReportBoxVisible] = useState(false);
    const [reportedPostId, setReportedPostId] = useState(null);

    const handleSettingsClick = () => {
        if (isAuthenticated) {
            // Якщо користувач авторизований, показуємо компонент Settings
            dispatch(toggleSettings());
        } else {
            // Якщо користувач не авторизований, показуємо компонент Authorize
            dispatch(toggleAuthorize());
        }
    };

    const handlePopAnimation = useCallback(
        (query) => {
            const item = document.querySelectorAll(query)[activeIndex];
            item.style.animation = `pop ease-in-out 500ms`;
            setTimeout(() => (item.style.animation = 'none'), 500);
        },
        [slides, activeIndex]
    );

    const handleToggleFavourites = useCallback(
        async (postId) => {
            if (!isAuthenticated) {
                // If the user is not authenticated, show the authorization component
                dispatch(toggleAuthorize());
                return;
            }

            handlePopAnimation('.comments .favourites');

            const result = await apiRequest('toggle_favorites.php', 'POST', {
                post_id: postId,
            });
            if (result.status == 'success') {
                setSlides((prevSlides) =>
                    prevSlides.map((slide) =>
                        slide.id === postId
                            ? {
                                  ...slide,
                                  is_favorite: result.is_favorite,
                              }
                            : slide
                    )
                );
            }
        },
        [slides, activeIndex]
    );

    const handleUserProfileClick = (userId) => {
        if (isAuthenticated) {
            console.log('handleUserProfileClick');
            // Якщо користувач авторизований, показуємо компонент User
            dispatch(setUser(userId));
            dispatch(toggleUserProfile());
        } else {
            // Якщо користувач не авторизований, показуємо компонент Authorize
            dispatch(toggleAuthorize());
        }
    };

    const handleTextCommentClick = (postId) => {
        if (isAuthenticated) {
            console.log('handleTextCommentClick');
            dispatch(setPostId(postId));
            dispatch(toggleTextComment());
        } else {
            // Якщо користувач не авторизований, показуємо компонент Authorize
            dispatch(toggleAuthorize());
        }
    };

    const handleLikeClick = useCallback(
        async (postId) => {
            if (!isAuthenticated) {
                // If the user is not authenticated, show the authorization component
                dispatch(toggleAuthorize());
                return;
            }

            if (isLiking[postId]) return; // Якщо слайд вже лайкається, виходимо з функції

            handlePopAnimation('.like_buttons .like');

            setIsLiking((prev) => ({ ...prev, [postId]: true })); // Встановлюємо стан лайкування слайда

            let response;
            try {
                response = await apiRequest('like_dislike_post.php', 'POST', {
                    post_id: postId,
                    like_dislike: 'like',
                });
            } catch (error) {
                console.error('Помилка при запиті до API:', error);
            }
            // Відправляємо запит на API

            if (response.status === 'success') {
                // Оновлюємо слайди
                setSlides((prevSlides) =>
                    prevSlides.map((slide) =>
                        slide.id === postId
                            ? {
                                  ...slide,
                                  is_liked: !slide.is_liked,
                                  like_count: response.like_count,
                              }
                            : slide
                    )
                );
            } else {
                console.error('Помилка при лайку поста:', response.message);
            }

            setIsLiking((prev) => ({ ...prev, [postId]: false })); // Вимикаємо стан лайкування слайда
        },
        [slides, activeIndex]
    );
    const handleDislikeClick = async (postId) => {
        if (!isAuthenticated) {
            // If the user is not authenticated, show the authorization component
            dispatch(toggleAuthorize());
            return;
        }

        if (isDisliking[postId]) return; // Якщо пост вже дизлайкається, виходимо з функції

        handlePopAnimation('.like_buttons .dislike');

        setIsDisliking((prev) => ({ ...prev, [postId]: true })); // Встановлюємо стан дизлайкування слайда

        let response;
        try {
            // Відправляємо запит на API для дизлайка
            response = await apiRequest('like_dislike_post.php', 'POST', {
                post_id: postId,
                like_dislike: 'dislike',
            });
        } catch (error) {
            console.error('Помилка при запиті до API:', error);
        }

        if (response.status === 'success') {
            // Оновлюємо слайди
            setSlides((prevSlides) =>
                prevSlides.map((slide) =>
                    slide.id === postId
                        ? {
                              ...slide,
                              is_disliked: !slide.is_disliked,
                              dislike_count: response.dislike_count,
                          }
                        : slide
                )
            );
        } else {
            console.error('Помилка при дизлайку поста:', response.message);
        }

        setIsDisliking((prev) => ({ ...prev, [postId]: false }));
    };

    useEffect(() => {
        console.log('useEffect...');
        //fetchSlides(1); // Завантаження першої сторінки при ініціалізації
        dispatch(setIsLoading(false));
    }, []);

    useEffect(() => {
        console.log('Updated slides:', slides);
    }, [slides]);

    const fetchSlides = async (currentPage) => {
        try {
            console.log(publisherId);
            const response = await apiRequest('get_posts.php', 'GET', {
                publisher_id: publisherId == -1 ? '' : publisherId,
                page: currentPage,
            });
            const newSlides = response.posts; // Отримання масиву posts з відповіді
            if (newSlides && newSlides.length) {
                setSlides((prevSlides) => [...prevSlides, ...newSlides]);
                setPage(currentPage);
                setCurrentPostId(newSlides[0].id);
                console.log('New fetched slides:', newSlides); // Виводить нові слайди для перевірки
            }
        } catch (error) {
            console.error('Failed to fetch slides:', error);
        }
    };

    const handleOpenUserPosts = useCallback(async () => {
        console.log(publisherId, slidePage);
        apiRequest('get_posts.php', 'GET', {
            publisher_id: publisherId == -1 ? '' : publisherId,
            page: 1,
        })
            .then((response) => {
                const newSlides = response.posts; // Отримання масиву posts з відповіді
                if (newSlides && newSlides.length) {
                    setSlides(newSlides);
                    setCurrentPostId(newSlides[0].id);
                    console.log('New fetched slides:', newSlides); // Виводить нові слайди для перевірки
                }
            })
            .catch((error) => console.error('Failed to fetch slides:', error));
    }, [publisherId, slidePage, setPage, setSlides, setCurrentPostId]);

    useEffect(() => {
        handleOpenUserPosts();
    }, [publisherId]);

    const handleSlideChange = (swiper) => {
        if (slides.length) setCurrentPostId(slides[swiper.activeIndex].id);
        setActiveIndex(swiper.activeIndex);
        // Перевірка чи користувач досягнув передостаннього слайда
        if (swiper.activeIndex + 2 >= slides.length) {
            fetchSlides(page + 1); // Завантаження наступної сторінки слайдів
        }
    };

    const handleHideUIClick = () => {
        setIsUIVisible(!isUIVisible);
    };

    const handleReportClick = (postId) => {
        setReportedPostId(postId);
        setIsReportBoxVisible(true);
    };

    const handleReportSubmit = async (data) => {
        try {
            // Make an API request to report the post
            const response = await apiRequest('report_post.php', 'POST', {
                post_id: data.postId, // Ensure this is the ID of the post being reported
                reason: data.reason, // The reason selected by the user
                additional_info: data.additionalInfo || '', // Optional additional information
            });

            if (response.status === 'success') {
                // Handle success response
                console.log('Report submitted successfully:', response);
                //alert('Your report has been submitted successfully.');
            } else {
                // Handle error response
                console.error('Failed to submit report:', response.message);
                //alert(`Failed to submit report: ${response.message}`);
            }
        } catch (error) {
            // Handle any errors
            console.error('Error while submitting report:', error);
            //alert('An error occurred while submitting your report. Please try again later.');
        }
        setIsReportBoxVisible(false);
    };

    return (
        <>
            {audioComments && (
                <AudioComment setAudioComments={setAudioComments} postId={currentPostId} />
            )}
            {isReportBoxVisible && (
                <ReportLightbox
                    postId={reportedPostId}
                    onClose={() => setIsReportBoxVisible(false)}
                    onSubmit={handleReportSubmit}
                />
            )}
            <Swiper
                direction={'vertical'}
                slidesPerView={1}
                mousewheel={{ enabled: true }}
                spaceBetween={0}
                className='mySwiper'
                id='content'
                initialSlide={slidePage}
                onSlideChange={handleSlideChange} // Обробник зміни слайда
            >
                <div className='top_nav'>
                    <div
                        className='button_left'
                        onClick={() => {
                            if (publisherId == -1) setCameraOn(true);
                            else {
                                dispatch(updatePublisherId(-1));
                                dispatch(updateSlidePage(1));
                            }
                        }}
                        style={{ transform: 'scale(1.3)' }}
                    >
                        {isUIVisible ? (
                            publisherId == -1 ? (
                                <svg
                                    className='hideable svg-shadow'
                                    id='make_post'
                                    xmlns='http://www.w3.org/2000/svg'
                                    xmlSpace='preserve'
                                    style={{
                                        width: '20px',
                                        fillRule: 'evenodd',
                                        clipRule: 'evenodd',
                                    }}
                                    viewBox='0 0 17500 14000'
                                >
                                    <path
                                        className='fil0'
                                        d='M14448.43 3671.25c-1137.19,234.01 -769.27,1929.47 339.6,1710.34 1138.66,-225 792.07,-1943.23 -339.6,-1710.34zm-9768.62 -2006.48c-603.72,98.77 -2502.17,-93.13 -3132.66,66.31 -809.17,204.63 -1407.87,891.98 -1516.44,1689.04 -57.62,422.95 -19.18,7082.76 -19.18,7987.61 0.01,575.28 -24.77,921.5 221.16,1401.68 156.33,305.22 472.76,644.39 777.37,813.95 456.51,254.12 738.33,283.89 1331.73,283.86l12819.14 0c543.59,0.03 906.74,-25.83 1323.25,-267.42 308.54,-178.96 606.74,-497.54 781.74,-834.08 234.3,-450.57 225.49,-829.03 225.48,-1372.75 -0.01,-911.77 37.05,-7581.59 -18.68,-8017.41 -108.54,-848.76 -759.55,-1493.67 -1517.09,-1684.6 -416.8,-105.05 -2564.01,-53.87 -3149.99,-56.55 -22.71,-602.07 -316.79,-1062.06 -767.9,-1325.75 -563.3,-329.26 -1161.48,-224.14 -1873.24,-224.14 -627.54,0 -3838.8,-36.6 -4190.18,27.79 -708.84,129.89 -1254.62,715.55 -1294.51,1512.46zm3828.95 2013.25c-865.05,52.3 -1608.71,358.49 -2258.19,837.71 -310.87,229.39 -282.98,250.08 -538.11,497.39 -298.54,289.4 -596.23,770.54 -791.89,1252.23 -1094.38,2694.18 992.84,5842.37 4108.66,5636.78 2917.86,-192.54 4741.06,-3349.25 3395.12,-5984.03 -345.47,-676.3 -849.75,-1209.37 -1485.23,-1619.26 -677.3,-436.86 -1590.09,-671.61 -2430.36,-620.82zm-166.87 1054.02c-820.94,104.71 -1493.09,509.89 -1986.06,1100.11 -786.53,941.66 -947.58,2357.88 -298.41,3526.05 929.57,1672.71 3451.06,2248.29 5088.83,432.77 471,-522.11 795.92,-1428.95 683.7,-2371.62 -189.57,-1592.31 -1654.14,-2921.2 -3488.06,-2687.31zm-2189.9 -3604.4c-653.31,146.35 -344.15,895.36 -473.56,1225.64 -83.52,213.14 -241.71,319.94 -534.66,323.16l-2549.63 -0.7c-695.13,0 -950.2,-57.22 -1350.18,366.2 -305.11,323 -227.52,934.31 -227.52,1451.53 0,847.54 -37.41,7360.95 23.25,7634.46 51.41,231.79 202.34,435.05 326.54,533.34 422.16,334.07 848.96,244.41 1455.02,244.41l11961.16 0c574.45,0 1067.95,45.75 1409.28,-290.47 335.57,-330.56 313.66,-838.12 313.66,-1409.35 0,-2271.11 0,-4542.22 0,-6813.32 0,-593.43 64.58,-1050.16 -280.44,-1417.47 -321.84,-342.65 -846.17,-299.33 -1417.26,-299.33l-2547.13 -3.95c-656.96,-66.54 -377.82,-860.23 -492.38,-1195.43 -68.42,-200.23 -245,-343.05 -492.87,-363.81 -442.72,-37.07 -2782.8,-3.05 -3432.35,-3.05 -364.26,0 -1408.09,-45.21 -1690.93,18.14z'
                                    ></path>
                                </svg>
                            ) : (
                                <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    xmlSpace='preserve'
                                    viewBox='0 0 501 501'
                                >
                                    <path d='M37.29 6.53l212.97 212.96 212.97 -212.98c8.44,-8.43 22.24,-8.43 30.67,0l0.02 0.03c8.45,8.43 8.45,22.24 0,30.67l-212.96 212.97 212.96 212.97c8.44,8.44 8.44,22.24 0,30.67l-0.02 0.02c-8.43,8.45 -22.24,8.45 -30.67,0l-212.97 -212.96 -212.96 212.95c-8.44,8.45 -22.24,8.45 -30.68,0l-0.01 -0.01c-8.45,-8.44 -8.45,-22.24 0,-30.67l212.95 -212.97 -212.95 -212.96c-8.45,-8.44 -8.45,-22.24 0,-30.68l0.01 -0.01c8.44,-8.45 22.24,-8.45 30.67,0z' />
                                </svg>
                            )
                        ) : (
                            <></>
                        )}
                    </div>
                    <div className='button_right'>
                        <div className='hide_interface' onClick={handleHideUIClick}>
                            {isUIVisible ? (
                                <svg
                                    className='hide_ui svg-shadow'
                                    xmlns='http://www.w3.org/2000/svg'
                                    viewBox='0 0 20500 14000'
                                    style={{ fillRule: 'evenodd' }}
                                >
                                    <path
                                        className='fil0'
                                        d='M10236.76 1692.32c5624.31,0 10061.53,5131.98 10183.71,5305.3 -147.88,145.19 -4559.4,5305.31 -10183.71,5305.31 -5624.31,0 -10040.55,-5124.32 -10183.71,-5305.31 118.55,-218.1 4559.4,-5305.3 10183.71,-5305.3zm-24.62 1274.46c2213.9,0 4008.62,1794.72 4008.62,4008.61 0,2213.9 -1794.72,4008.62 -4008.62,4008.62 -2213.89,0 -4008.61,-1794.72 -4008.61,-4008.62 0,-2213.89 1794.72,-4008.61 4008.61,-4008.61zm0 1911.69c1158.1,0 2096.93,938.83 2096.93,2096.92 0,1158.1 -938.83,2096.93 -2096.93,2096.93 -1158.1,0 -2096.92,-938.83 -2096.92,-2096.93 0,-1158.09 938.82,-2096.92 2096.92,-2096.92z'
                                    />
                                </svg>
                            ) : (
                                <svg
                                    className='unhide_ui svg-shadow'
                                    xmlns='http://www.w3.org/2000/svg'
                                    viewBox='0 0 20500 14000'
                                    style={{ fillRule: 'evenodd' }}
                                >
                                    <path
                                        className='fil0'
                                        d='M10236.76 1692.32c5624.31,0 10061.53,5131.98 10183.71,5305.3 -85.28,83.73 -1588.7,1835.24 -3924.79,3303.14l-2385.91 -2385.91c72.38,-301.37 110.99,-615.89 110.99,-939.46 0,-2213.89 -1794.72,-4008.61 -4008.62,-4008.61 -323.57,0 -638.08,38.61 -939.45,110.99l-1151.26 -1151.26c680.88,-148.59 1388.09,-234.19 2115.33,-234.19zm-5932.48 -1600.24l2284.57 2284.57 9.24 -3.47 1316.94 1316.94 -7.43 5.28 1391.76 1391.76 8.53 -4.17 2796.66 2796.66 -4.17 8.53 1391.76 1391.76 5.28 -7.42 1740.83 1740.83 -8.45 4.25 1900.58 1900.58 -985.01 985.01 -2285.24 -2285.24c-1120.51,416.33 -2340.33,684.98 -3623.37,684.98 -5624.31,0 -10040.55,-5124.32 -10183.71,-5305.31 79.84,-146.88 2120.44,-2501.91 5162.49,-4024.27l-1896.26 -1896.26 985 -985.01zm8201.25 10171.27l-1393.49 -1393.5c-272.68,129.78 -577.8,202.47 -899.9,202.47 -1158.1,0 -2096.92,-938.83 -2096.92,-2096.93 0,-322.1 72.69,-627.21 202.47,-899.89l-1393.5 -1393.5c-454.18,649.78 -720.66,1440.44 -720.66,2293.39 0,2213.9 1794.72,4008.62 4008.61,4008.62 852.95,0 1643.61,-266.48 2293.39,-720.66zm-918.36 -4871.1c74.15,64.46 143.73,134.03 208.19,208.19l-208.19 -208.19z'
                                    />
                                </svg>
                            )}
                        </div>
                        <svg
                            className='sett svg-shadow'
                            onClick={handleSettingsClick}
                            xmlns='http://www.w3.org/2000/svg'
                            viewBox='0 0 2500 12000'
                            style={{ fillRule: 'evenodd', clipRule: 'evenodd' }}
                        >
                            <path
                                className='fil0'
                                d='M1262.94 235.68c674.15,0 1220.66,546.51 1220.66,1220.67 0,674.15 -546.51,1220.66 -1220.66,1220.66 -674.16,0 -1220.67,-546.51 -1220.67,-1220.66 0,-674.16 546.51,-1220.67 1220.67,-1220.67zm0 9180.52c674.15,0 1220.66,546.51 1220.66,1220.66 0,674.16 -546.51,1220.67 -1220.66,1220.67 -674.16,0 -1220.67,-546.51 -1220.67,-1220.67 0,-674.15 546.51,-1220.66 1220.67,-1220.66zm0 -4590.28c674.15,0 1220.66,546.51 1220.66,1220.67 0,674.15 -546.51,1220.66 -1220.66,1220.66 -674.16,0 -1220.67,-546.51 -1220.67,-1220.66 0,-674.16 546.51,-1220.67 1220.67,-1220.67z'
                            />
                        </svg>
                    </div>
                </div>
                {console.log(slides)}
                {slides.map((slide, index) => {
                    // Логіка для кожного слайда
                    const showTranslate = slide.description.length !== 0;
                    const showMore = slide.description.length > 35;

                    return (
                        <SwiperSlide key={index} className='post' style={{ height: '100svh' }}>
                            <div className='media'>
                                {slide.type == 'video' ? (
                                    <video
                                        src={`https://letmestyle.com${slide.video_url}`}
                                        type='vide/webm'
                                        loop
                                        autoPlay={true}
                                        playsinline
                                        muted
                                        style={{
                                            width: 'auto',
                                            height: '100svh',
                                            objectFit: 'cover',
                                        }}
                                    ></video>
                                ) : (
                                    <img
                                        className='media_content'
                                        src={slide.pic}
                                        alt='Slide Picture'
                                        style={{
                                            width: '100%',
                                            height: '100svh',
                                            objectFit: 'cover',
                                        }}
                                    />
                                )}
                            </div>
                            {isUIVisible ? (
                                <div className='author_comment hideable'>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                        }}
                                    >
                                        <a
                                            className='user_profile'
                                            data-userid={slide.user_id}
                                            onClick={() => handleUserProfileClick(slide.user_id)}
                                        >
                                            <img
                                                src={
                                                    slide.avatar || '/img/icons/default_avatar.png'
                                                }
                                                alt='User Avatar'
                                            />
                                            <span>{slide.nickname}</span>
                                        </a>
                                        <span
                                            className='report'
                                            data-postid={`${slide.id}`}
                                            onClick={() => handleReportClick(slide.id)}
                                        >
                                            Report
                                        </span>
                                    </div>
                                    <div className='com_text_button'>
                                        <div>{slide.description}</div>
                                    </div>
                                </div>
                            ) : null}

                            <div
                                className='interface default hideable'
                                style={{ height: 'calc(100% - 95px)' }}
                            >
                                {isUIVisible ? (
                                    <div className='right_i'>
                                        <div className='comments' data-postid={slide.id}>
                                            <div
                                                className='comm_text_button ani'
                                                onClick={() => handleTextCommentClick(slide.id)}
                                            >
                                                <svg
                                                    width='27'
                                                    height='27'
                                                    className='svg-shadow'
                                                    xmlns='http://www.w3.org/2000/svg'
                                                    style={{
                                                        fillRule: 'evenodd',
                                                        clipRule: 'evenodd',
                                                    }}
                                                    viewBox='0 0 17000 15000'
                                                >
                                                    <path
                                                        className='fil0'
                                                        d='M7711.23 181.84c-2057.47,201.96 -3444.86,691.19 -5006.38,1842.2 -682.02,502.73 -1309.62,1235.05 -1767.27,1949.58 -929.91,1451.87 -1111.7,3485.62 -478.47,5074.04 234.31,587.74 435.56,922.89 736.38,1373.18 393.7,589.31 614.26,318.14 264.4,1476.81 -541.7,1794.03 -1686.38,2146.22 -1311.91,2750.57 178.13,287.48 402.98,268.85 783.13,231.68 1474.12,-144.11 2658.57,-610.01 3872.22,-1414.32 518.6,-343.68 118.36,-150.63 1553.15,163.74 2449.45,536.66 5066.54,76.83 6982.37,-1003.79 3000.15,-1692.25 4609.89,-5082.8 2908.45,-8349.83 -257.74,-494.91 -557.87,-879.93 -856.9,-1251.79 -147.54,-183.48 -353.75,-336.24 -520.81,-518.06 -713.47,-776.51 -2210.86,-1554.8 -3370.41,-1919.19 -1070.15,-336.29 -2608.66,-520.58 -3787.95,-404.82zm-3662.18 5790.29c-1343.54,306.01 -931.75,2375.98 502.58,2052.49 1312.56,-296.02 900.18,-2371.99 -502.58,-2052.49zm4219.32 0c-1343.54,306.01 -931.75,2375.98 502.59,2052.49 1312.55,-296.02 900.17,-2371.99 -502.59,-2052.49zm4219.31 0c-1343.53,306.01 -931.73,2375.98 502.6,2052.49 521.04,-117.51 917.95,-617.04 764.04,-1288.83 -120.93,-527.87 -640,-906.38 -1266.64,-763.66z'
                                                    ></path>
                                                </svg>
                                                <span className='t-shadow'>
                                                    {slide.text_comments_count || 0}
                                                </span>
                                            </div>
                                            <div
                                                className='comm_voice_button ani'
                                                onClick={() => setAudioComments(true)}
                                            >
                                                <img
                                                    src='/svg/voice-comment.svg'
                                                    alt='voice-comments'
                                                    style={{
                                                        width: '27px',
                                                        height: '27px',
                                                    }}
                                                />
                                                <span className='t-shadow'>
                                                    {slide.voice_comments_count || 0}
                                                </span>
                                            </div>
                                            <div
                                                className={`favourites ani ${
                                                    slide.is_favorite ? 'active' : ''
                                                }`}
                                                onClick={() => handleToggleFavourites(slide.id)}
                                            >
                                                <svg
                                                    width='27'
                                                    height='27'
                                                    className='svg-shadow'
                                                    xmlns='http://www.w3.org/2000/svg'
                                                    viewBox='0 0 2601 2501'
                                                >
                                                    <defs>
                                                        <linearGradient
                                                            id='favourite-gradient'
                                                            gradientTransform='rotate(30)'
                                                        >
                                                            <stop
                                                                offset='0%'
                                                                stopColor='#e70489'
                                                            ></stop>
                                                            <stop
                                                                offset='100%'
                                                                stopColor='#fa5f6f'
                                                            ></stop>
                                                        </linearGradient>
                                                    </defs>
                                                    <path d='M1302.19 3.25c98.33,-0.07 130.5,71.25 195.75,210.87l253.03 541.42c196.72,24.86 393.44,49.72 590.16,74.58 152.33,19.25 229.37,37.58 253.45,113.53 24.08,75.94 -21.6,156.51 -134.18,262.66l-432.07 407.38c36.87,193.63 73.73,387.26 110.6,580.9 29.27,153.72 29.3,238.09 -30.67,281.44 -59.97,43.36 -155.54,28.07 -292.55,-47.76l-513.52 -284.22c-171.08,94.69 -342.15,189.37 -513.23,284.06 -137.1,75.88 -235.41,89.96 -288.88,51.46 -53.47,-38.5 -63.97,-130.91 -34.73,-284.46l110.7 -581.42c-143.66,-135.44 -287.3,-270.89 -430.95,-406.33 -112.95,-106.5 -162.11,-173.82 -138.46,-262.54 23.64,-88.72 105.74,-95.64 258.81,-114.98l587.96 -74.3c84.3,-180.38 168.6,-360.76 252.9,-541.14 65.29,-139.72 97.55,-211.08 195.88,-211.15z'></path>
                                                </svg>
                                            </div>
                                            <br />
                                            <div className='share ani'>
                                                <svg
                                                    width='27'
                                                    height='27'
                                                    className='svg-shadow'
                                                    xmlns='http://www.w3.org/2000/svg'
                                                    viewBox='0 0 512 512'
                                                >
                                                    <path d='M307 34.8c-11.5 5.1-19 16.6-19 29.2v64H176C78.8 128 0 206.8 0 304C0 417.3 81.5 467.9 100.2 478.1c2.5 1.4 5.3 1.9 8.1 1.9c10.9 0 19.7-8.9 19.7-19.7c0-7.5-4.3-14.4-9.8-19.5C108.8 431.9 96 414.4 96 384c0-53 43-96 96-96h96v64c0 12.6 7.4 24.1 19 29.2s25 3 34.4-5.4l160-144c6.7-6.1 10.6-14.7 10.6-23.8s-3.8-17.7-10.6-23.8l-160-144c-9.4-8.5-22.9-10.6-34.4-5.4z'></path>
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                {isUIVisible ? (
                                    <div className='like_buttons' data-postid='{slide.id}'>
                                        <div className='like_count'>{slide.like_count || 0}</div>
                                        <div
                                            className={`like ani ${
                                                slide.is_liked ? 'like_liked' : ''
                                            }`}
                                            onClick={async () => {
                                                if (slide.is_disliked)
                                                    await handleDislikeClick(slide.id);
                                                handleLikeClick(slide.id);
                                            }}
                                        >
                                            <svg
                                                style={{ fillRule: 'evenodd', clipRule: 'evenodd' }}
                                                viewBox='0 0 4300 4400'
                                            >
                                                <path
                                                    className='fil0'
                                                    d='M1134.52 2273.76c0,-229.3 282.23,-471.84 423.34,-707.76 134.79,-191.66 322.78,-483.78 328.13,-815.49 5.35,-331.71 -139.6,-718.81 157.97,-708.89 584,19.47 728.75,1029.48 473.01,1514.46 241.52,0 1072.6,0.02 1216.93,0.02 563.76,0 604.06,573.82 287.51,726.52 386.41,124.16 365.4,676.58 -64.92,733.18 204.24,142.4 213.37,582.48 -280.23,638.87 192.35,204.47 159.8,643.16 -603.66,643.16l-1435.37 0c-256.86,0 -502.71,-133.95 -502.71,-416.72 0,-535.78 0,-1071.57 0,-1607.35zm-994.48 -211.66l566.82 0c71.5,0 130,58.5 130,130l0 2061.72c0,71.5 -58.5,130 -130,130l-566.82 0c-71.5,0 -130,-58.5 -130,-130l0 -2061.72c0,-71.5 58.5,-130 130,-130z'
                                                />
                                            </svg>
                                        </div>
                                        <div
                                            className={`dislike ani ${
                                                slide.is_disliked ? 'dislike_liked' : ''
                                            }`}
                                            onClick={async () => {
                                                if (slide.is_liked) await handleLikeClick(slide.id);
                                                handleDislikeClick(slide.id);
                                            }}
                                        >
                                            <svg
                                                xmlns='http://www.w3.org/2000/svg'
                                                style={{ fillRule: 'evenodd', clipRule: 'evenodd' }}
                                                viewBox='0 0 4300 4400'
                                            >
                                                <defs>
                                                    <linearGradient
                                                        id='dislike-gradient'
                                                        gradientTransform='rotate(30)'
                                                    >
                                                        <stop
                                                            offset='0%'
                                                            stopColor='#e70489'
                                                        ></stop>
                                                        <stop
                                                            offset='100%'
                                                            stopColor='#fa5f6f'
                                                        ></stop>
                                                    </linearGradient>
                                                </defs>
                                                <path
                                                    className={`gradientable5${slide.id}`}
                                                    d='M3171.6 2151.48c0,229.3 -282.23,471.84 -423.34,707.76 -134.79,191.66 -322.78,483.78 -328.13,815.49 -5.35,331.71 139.6,718.81 -157.97,708.89 -584,-19.47 -728.75,-1029.48 -473.01,-1514.46 -241.52,0 -1072.6,-0.02 -1216.93,-0.02 -563.76,0 -604.06,-573.82 -287.51,-726.52 -386.41,-124.16 -365.4,-676.58 64.92,-733.18 -204.24,-142.4 -213.37,-582.48 280.23,-638.87 -192.35,-204.47 -159.8,-643.16 603.66,-643.16l1435.37 0c256.86,0 502.71,133.95 502.71,416.72 0,535.78 0,1071.57 0,1607.35zm994.48 211.66l-566.82 0c-71.5,0 -130,-58.5 -130,-130l0 -2061.72c0,-71.5 58.5,-130 130,-130l566.82 0c71.5,0 130,58.5 130,130l0 2061.72c0,71.5 -58.5,130 -130,130z'
                                                ></path>
                                            </svg>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </>
    );
}

export default Swiper_block;
